import React, { useEffect, useMemo, useState } from "react";
import { supabase } from "./supabaseClient";
import { useNavigate } from "react-router-dom";
import "./App.css";
import { useTranslation } from "react-i18next";
import { FiChevronLeft, FiChevronRight } from "react-icons/fi";

const Statistics = ({ userId }) => {
  const { t } = useTranslation();
  const [logs, setLogs] = useState([]);
  const [average, setAverage] = useState(null);
  const [searchText, setSearchText] = useState("");
  const navigate = useNavigate();

  const PAGE_SIZE = 10;
  const [page, setPage] = useState(1);

  const prevStep = () => setPage((p) => Math.max(1, p - 1));
  const nextStep = () => setPage((p) => Math.min(totalPages, p + 1));

  useEffect(() => {
    const fetchLogs = async () => {
      const { data, error } = await supabase
        .from("insulin_logs")
        .select(
          "id, carboidrati, insulina_calcolata, glicemia, created_at, image_url, risultato_ai, is_visible"
        )
        .eq("user_id", userId)
        .eq("is_visible", true)
        .order("created_at", { ascending: false });

      if (error) {
        console.error("Errore nel recupero dei log:", error.message);
        return;
      }

      const rows = data || [];
      setLogs(rows);

      const total = rows.reduce(
        (sum, log) => sum + (Number(log.insulina_calcolata) || 0),
        0
      );
      const avg = rows.length ? total / rows.length : 0;
      setAverage(Math.round(avg * 10) / 10);
    };

    if (userId) fetchLogs();
  }, [userId]);

  useEffect(() => {
    setPage(1);
  }, [searchText]);

  const formatTime = (isoString) => {
    const utcString = isoString?.endsWith("Z") ? isoString : isoString + "Z";
    const dateUTC = new Date(utcString);
    const options = {
      day: "2-digit",
      month: "short",
      hour: "2-digit",
      minute: "2-digit",
      timeZone: "Europe/Rome",
    };
    return dateUTC.toLocaleString("it-IT", options);
  };

  const normalizeText = (s = "") =>
    s
      .toLowerCase()
      .normalize("NFD")
      .replace(/[\u0300-\u036f]/g, "")
      .replace(/[^\w\s"]+/g, " ")
      .replace(/\s+/g, " ")
      .trim();

  const filteredLogs = useMemo(() => {
    const raw = searchText.trim();
    if (!raw) return logs;

    const text = normalizeText(raw);
    const isQuoted =
      text.startsWith('"') && text.endsWith('"') && text.length >= 2;

    return logs.filter((log) => {
      const hay = normalizeText(log.risultato_ai || "");

      if (isQuoted) {
        const phrase = text.slice(1, -1).trim();
        if (!phrase) return true;
        return hay.includes(phrase);
      }

      const tokens = text.split(" ").filter(Boolean);
      return tokens.every((tok) => hay.includes(tok));
    });
  }, [logs, searchText]);

  const totalPages = Math.max(1, Math.ceil(filteredLogs.length / PAGE_SIZE));

  const paginatedLogs = useMemo(() => {
    const start = (page - 1) * PAGE_SIZE;
    return filteredLogs.slice(start, start + PAGE_SIZE);
  }, [filteredLogs, page]);

  const stats = useMemo(() => {
    const totalAnalyses = logs.length;

    const dayCount = new Map();
    logs.forEach((log) => {
      if (!log.created_at) return;
      const utcString = log.created_at.endsWith("Z")
        ? log.created_at
        : log.created_at + "Z";
      const d = new Date(utcString);

      const key = d.toLocaleDateString("it-IT", {
        timeZone: "Europe/Rome",
        year: "numeric",
        month: "2-digit",
        day: "2-digit",
      });

      dayCount.set(key, (dayCount.get(key) || 0) + 1);
    });

    let bestDay = null;
    let bestDayCount = 0;
    for (const [day, count] of dayCount.entries()) {
      if (count > bestDayCount) {
        bestDay = day;
        bestDayCount = count;
      }
    }

    const withCarbs = logs
      .map((l) => ({
        ...l,
        carbo_num:
          l.carboidrati === null || l.carboidrati === undefined
            ? null
            : Number(l.carboidrati),
      }))
      .filter((l) => Number.isFinite(l.carbo_num));

    let mostCarbs = null;
    let leastCarbs = null;
    withCarbs.forEach((l) => {
      if (!mostCarbs || l.carbo_num > mostCarbs.carbo_num) mostCarbs = l;
      if (!leastCarbs || l.carbo_num < leastCarbs.carbo_num) leastCarbs = l;
    });

    return {
      totalAnalyses,
      bestDay,
      bestDayCount,
      mostCarbs,
      leastCarbs,
    };
  }, [logs]);

  const MealCard = ({ title, log }) => {
    if (!log) {
      return (
        <div className="stats-inset">
          <div style={{ fontWeight: 800, marginBottom: 6, textAlign: "center" }}>
            {title}
          </div>
          <div className="instructions" style={{ margin: 0, textAlign: "center" }}>
            {t("nessunDato")}
          </div>
        </div>
      );
    }

    return (
      <div
        className="stats-inset clickable"
        onClick={() => navigate(`/log/${log.id}`)}
        title={t("apriDettaglio") || "Apri dettaglio"}
      >
        <div style={{ fontWeight: 800, marginBottom: 8, textAlign: "center", fontSize: "1.05rem" }}>
          {title}
        </div>

        {/* ✅ layout stabile: immagine sopra e testo sotto (centrati) */}
        <div
          style={{
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            textAlign: "center",
            gap: 10,
          }}
        >
          {log.image_url ? (
            <img
              src={log.image_url}
              alt={t("pasto")}
              onError={(e) => {
                e.target.src = "/placeholder.png";
              }}
              style={{
                width: 72,
                height: 72,
                objectFit: "cover",
                borderRadius: 10,
              }}
            />
          ) : (
            <div
              style={{
                width: 72,
                height: 72,
                borderRadius: 10,
                background: "rgba(0,0,0,0.08)",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                fontSize: 12,
              }}
            >
              {t("nessunaFoto") || "No photo"}
            </div>
          )}

          <div className="meal-meta">
            <p className="meal-macro">
              <span className="label">{t("carboidrati")}</span>
              {Number.isFinite(log.carbo_num) ? `${log.carbo_num} g` : "-"}
            </p>
            <p className="meal-date">{formatTime(log.created_at)}</p>
          </div>
        </div>
      </div>
    );
  };

  return (
    <div className="container">
      <div className="header-with-button">
        <h1 className="title">{t("statistiche")}</h1>
      </div>

      <div className="card-section card-blue">
        <div
          style={{
            display: "flex",
            flexDirection: "column",
            gap: 12,
            marginTop: 10,
          }}
        >
        <div className="stats-inset">
          <div className="stats-box">
            <p className="stats-kicker">{t("analisiTotali")}</p>
            <p className="stats-value">{stats.totalAnalyses}</p>
          </div>
        </div>

        <div className="stats-inset">
          <div className="stats-box">
            <p className="stats-kicker">{t("mediaUnitaInsuliniche")}</p>

            {average !== null ? (
              <p className="stats-value">
                {average}
                <span className="unit">u</span>
              </p>
            ) : (
              <p className="stats-sub">{t("caricamento") || "Caricamento..."}</p>
            )}
          </div>
        </div>

        <div className="stats-inset">
          <div className="stats-box">
            <p className="stats-kicker">{t("giornoPiuAnalisi") || "Giorno con più analisi"}</p>

            {stats.bestDay ? (
              <p className="stats-sub">
                <span className="big">{stats.bestDayCount}</span>{" "}
                {t("analisiNelGiorno") || "analisi il"}{" "}
                <b>{stats.bestDay}</b>
              </p>
            ) : (
              <p className="stats-sub">{t("nessunDato")}</p>
            )}
          </div>
        </div>


          <MealCard title={t("pastoPiuGlicemico")} log={stats.mostCarbs} />
          <MealCard title={t("pastoMenoGlicemico")} log={stats.leastCarbs} />
        </div>
      </div>

      {/* ✅ RICERCA + TABELLA NELLA STESSA CARD */}
      <div className="card-section card-green">
        <h2 className="titlesec">{t("cercaDescrizione")}</h2>

        {/* Se vuoi “incassare” anche la ricerca, wrappa così */}
        <div style={{ marginTop: 10 }}>
          <input
            className="form-input"
            value={searchText}
            onChange={(e) => setSearchText(e.target.value)}
            placeholder={t("scriviQui")}
          />
          <div className="instructions" style={{ marginTop: 8, opacity: 0.85 }}>
             {t("risultatiFiltrati")}: <span style={{ fontWeight: 600 }}>{filteredLogs.length}</span>
          </div>
        </div>

        {paginatedLogs.length > 0 ? (
          <div style={{ marginTop: 14 }}>
            <div className="stats-list">
              {paginatedLogs.map((log) => (
                <div
                  key={log.id}
                  className="stats-rowcard"
                  onClick={() => navigate(`/log/${log.id}`)}
                  role="button"
                  tabIndex={0}
                  onKeyDown={(e) => {
                    if (e.key === "Enter" || e.key === " ") navigate(`/log/${log.id}`);
                  }}
                  style={{ cursor: "pointer" }}
                >
                  {/* thumb */}
                  <div className="stats-cell stats-cell-thumb">
                    {log.image_url ? (
                      <img
                        src={log.image_url}
                        alt={t("pasto")}
                        onError={(e) => {
                          e.currentTarget.src = "/placeholder.png";
                        }}
                        className="stats-thumb"
                      />
                    ) : (
                      <div className="stats-thumb stats-thumb--placeholder">-</div>
                    )}
                  </div>

                  {/* glicemia */}
                  <div className="stats-cell">
                    <div className="stats-cell-label">{t("glicemia")}</div>
                    <div className="stats-cell-value">{log.glicemia || "-"}</div>
                  </div>

                  {/* insulina */}
                  <div className="stats-cell">
                    <div className="stats-cell-label">{t("insulina")}</div>
                    <div className="stats-cell-value">
                      {log.insulina_calcolata ? `${log.insulina_calcolata} u` : "-"}
                    </div>
                  </div>

                  {/* data */}
                  <div className="stats-cell stats-cell-date">
                    <div className="stats-cell-label">{t("orario")}</div>
                    <div className="stats-cell-value">{formatTime(log.created_at)}</div>
                  </div>
                </div>
              ))}
            </div>

            {/* paginazione: uguale a prima */}
            <div
              style={{
                display: "flex",
                justifyContent: "center",
                alignItems: "center",
                gap: 10,
                marginTop: 12,
              }}
            >
              <button
                type="button"
                onClick={prevStep}
                className="icon-nav"
                aria-label={t("indietro")}
                disabled={page <= 1}
                style={{ opacity: page <= 1 ? 0.4 : 1 }}
              >
                <FiChevronLeft size={34} />
              </button>

              <div className="instructions" style={{ margin: 0 }}>
                {page} / {totalPages}
              </div>

              <button
                type="button"
                onClick={nextStep}
                className="icon-nav"
                aria-label={t("avanti")}
                disabled={page >= totalPages}
                style={{ opacity: page >= totalPages ? 0.4 : 1 }}
              >
                <FiChevronRight size={34} />
              </button>
            </div>
          </div>

        ) : (
          <p style={{ marginTop: 12 }}>{t("nessunDato")}</p>
        )}
      </div>
    </div>
  );

};

export default Statistics;
